/**
 * Página de Áreas da Empresa
 * Gerencia o cadastro e visualização das áreas da empresa
 * 
 * @author Equipe GestaoeCRM
 * @date 31/01/2025
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Edit, 
  Eye, 
  Users,
  Building2,
  Filter,
  X,
  Check,
  AlertCircle,
  User,
  Mail,
  Shield
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import companyAreaService, { 
  CompanyArea, 
  CreateAreaData, 
  UpdateAreaData, 
  AvailableManager,
  Pagination 
} from '@/services/companyAreaService';

export default function CompanyAreasPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedArea, setSelectedArea] = useState<CompanyArea | null>(null);
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  const [formData, setFormData] = useState<CreateAreaData & { active?: boolean }>({
    name: '',
    description: '',
    managerId: '',
    active: true
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  const queryClient = useQueryClient();

  // Buscar áreas com paginação
  const { data: areasResponse, isLoading } = useQuery({
    queryKey: ['company-areas', activeFilter, currentPage],
    queryFn: () => companyAreaService.getAreas({ active: activeFilter, page: currentPage, limit: pageSize }),
  });
  const areas: CompanyArea[] = (areasResponse as any)?.data ?? [];
  const pagination: Pagination | undefined = (areasResponse as any)?.pagination;

  // Buscar gestores disponíveis
  const { data: availableManagers = [] } = useQuery({
    queryKey: ['available-managers'],
    queryFn: () => companyAreaService.getAvailableManagers(),
  });

  // Mutação para criar área
  const createAreaMutation = useMutation({
    mutationFn: companyAreaService.createArea,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-areas'] });
      queryClient.invalidateQueries({ queryKey: ['available-managers'] });
      toast.success('Área criada com sucesso!');
      setShowCreateModal(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao criar área');
    },
  });

  // Mutação para atualizar área
  const updateAreaMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAreaData }) =>
      companyAreaService.updateArea(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-areas'] });
      queryClient.invalidateQueries({ queryKey: ['available-managers'] });
      toast.success('Área atualizada com sucesso!');
      setShowEditModal(false);
      resetForm();
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar área:', error);
      
      // Tratamento específico para erros de validação
      if (error.response?.status === 400) {
        const validationErrors = error.response?.data?.errors || error.response?.data?.details || [];
        if (validationErrors.length > 0) {
          const errorMessages = validationErrors.map((err: any) => 
            `${err.field || 'campo'}: ${err.message}`
          ).join(', ');
          console.error('Erro de validação detalhado:', {
            errors: validationErrors,
            receivedData: error.config?.data,
            url: error.config?.url
          });
          toast.error(`Erro de validação: ${errorMessages}`);
        } else {
          toast.error(error.response?.data?.message || 'Erro de validação');
        }
      } else if (error.response?.status === 404) {
        console.error('Área não encontrada. Verifique se o ID está correto:', {
          url: error.config?.url,
          areaId: error.config?.data ? JSON.parse(error.config.data)?.id : 'não disponível'
        });
        toast.error('Área não encontrada. Verifique se a área ainda existe.');
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar área';
        toast.error(errorMessage);
      }
    },
  });

  // Mutação para deletar área
  const deleteAreaMutation = useMutation({
    mutationFn: companyAreaService.deleteArea,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-areas'] });
      toast.success('Área desativada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao desativar área');
    },
  });

  // Filtrar áreas por termo de busca
  const filteredAreas = areas.filter(area =>
    area.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    area.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    area.manager?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      managerId: '',
      active: true
    });
    setSelectedArea(null);
  };

  const handleCreate = () => {
    setShowCreateModal(true);
    // Atualizar lista de gestores ao abrir modal
    queryClient.invalidateQueries({ queryKey: ['available-managers'] });
    resetForm();
  };

  const handleEdit = (area: CompanyArea) => {
    setSelectedArea(area);
    setFormData({
      name: area.name,
      description: area.description || '',
      managerId: area.managerId || '',
      active: area.active
    });
    setShowEditModal(true);
  };

  const handleView = (area: CompanyArea) => {
    // Navegar para página de detalhes
    navigate(`/administration/company-areas/${area.id}`);
  };


  const handleSubmitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    createAreaMutation.mutate(formData);
  };

  const handleSubmitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedArea || !selectedArea.id) {
      toast.error('Erro: Área não selecionada corretamente');
      return;
    }
    
    // Validar que o ID não está vazio
    if (!selectedArea.id.trim()) {
      toast.error('Erro: ID da área inválido');
      return;
    }

    // Log para debug
    console.log('Enviando atualização de área:', {
      id: selectedArea.id,
      data: formData,
      formDataTypes: {
        name: typeof formData.name,
        description: typeof formData.description,
        managerId: typeof formData.managerId,
        active: typeof formData.active
      }
    });

    updateAreaMutation.mutate({
      id: selectedArea.id,
      data: formData
    });
  };

  const getStatusBadge = (active: boolean) => {
    return active ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-500/20 text-green-400">
        <Check className="w-3 h-3 mr-1" />
        Ativa
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-500/20 text-red-400">
        <X className="w-3 h-3 mr-1" />
        Inativa
      </span>
    );
  };

  // Verificar se deve abrir modal automaticamente (vindo do onboarding)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('action') === 'create') {
      handleCreate();
      // Limpar parâmetro da URL
      navigate(location.pathname, { replace: true });
    }
  }, [location]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-semibold leading-6 text-text-primary sm:text-2xl sm:truncate">
            Áreas da Empresa
          </h2>
          <p className="mt-1 text-sm text-text-muted">
            Gerencie as áreas e departamentos da empresa
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-button shadow-glass text-sm font-bold text-white bg-gradient-to-r from-primary to-secondary hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-secondary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Área
          </button>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-background-glass backdrop-blur-glass shadow rounded-glass">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-muted" />
                <input
                  type="text"
                  placeholder="Buscar por nome, descrição ou gestor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                />
              </div>
            </div>
            <div>
              <select
                value={activeFilter === undefined ? '' : activeFilter.toString()}
                onChange={(e) => setActiveFilter(
                  e.target.value === '' ? undefined : e.target.value === 'true'
                )}
                className="block w-full border border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm px-3 py-2 appearance-none cursor-pointer hover:bg-background-glass-hover transition-colors font-bold"
                style={{
                  backgroundColor: 'rgba(17, 24, 39, 0.9)',
                  color: '#ffffff',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  appearance: 'none'
                }}
              >
                <option value="" style={{ backgroundColor: 'rgba(17, 24, 39, 0.95)', color: '#ffffff', padding: '8px' }}>Todas as áreas</option>
                <option value="true" style={{ backgroundColor: 'rgba(17, 24, 39, 0.95)', color: '#ffffff', padding: '8px' }}>Apenas ativas</option>
                <option value="false" style={{ backgroundColor: 'rgba(17, 24, 39, 0.95)', color: '#ffffff', padding: '8px' }}>Apenas inativas</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Áreas */}
      <div className="bg-background-glass backdrop-blur-glass shadow rounded-glass">
        <div className="px-6 py-4 border-b border-border-glass">
          <h3 className="text-lg font-bold text-text-primary">
            Áreas ({pagination ? pagination.total : filteredAreas.length})
          </h3>
        </div>
        <div className="divide-y divide-border-glass/50">
          {filteredAreas.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Building2 className="mx-auto h-12 w-12 text-text-muted" />
              <h3 className="mt-2 text-sm font-bold text-text-primary">Nenhuma área encontrada</h3>
              <p className="mt-1 text-sm text-text-muted">
                {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando uma nova área.'}
              </p>
            </div>
          ) : (
            filteredAreas.map((area) => (
              <div key={area.id} className="px-6 py-4 hover:bg-background-glass-hover">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h4 className="text-lg font-bold text-text-primary">{area.name}</h4>
                      {getStatusBadge(area.active)}
                    </div>
                    {area.description && (
                      <p className="mt-1 text-sm text-text-secondary">{area.description}</p>
                    )}
                    <div className="mt-2 flex items-center gap-4 text-sm text-text-muted">
                      {area.manager && (
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          <span>Gestor: {area.manager.name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{area._count?.users || 0} usuários</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleView(area)}
                      className="p-2 text-text-muted hover:text-secondary"
                      title="Visualizar detalhes"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(area)}
                      className="p-2 text-text-muted hover:text-yellow-400"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Paginação */}
      {pagination && pagination.totalPages > 1 && (
        <div className="bg-background-glass backdrop-blur-glass px-4 py-3 flex items-center justify-between border-t border-border-glass sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-border-glass text-sm font-bold rounded-button text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={currentPage === pagination.totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-border-glass text-sm font-bold rounded-button text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próximo
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-text-secondary">
                Mostrando{' '}
                <span className="font-bold">{(currentPage - 1) * pageSize + 1}</span>
                {' '}até{' '}
                <span className="font-bold">{Math.min(currentPage * pageSize, pagination.total)}</span>
                {' '}de{' '}
                <span className="font-bold">{pagination.total}</span>
                {' '}resultados
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-button shadow-glass -space-x-px">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-border-glass bg-background-glass backdrop-blur-glass text-sm font-bold text-text-muted hover:bg-background-glass-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={currentPage === pagination.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-border-glass bg-background-glass backdrop-blur-glass text-sm font-bold text-text-muted hover:bg-background-glass-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próximo
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-glass rounded-button bg-background-glass backdrop-blur-glass">
            <div className="mt-3">
              <h3 className="text-lg font-bold text-text-primary mb-4">
                Nova Área
              </h3>
              <form onSubmit={handleSubmitCreate} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-text-secondary">
                      Nome da Área *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                      placeholder="Ex: Recursos Humanos"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-text-secondary">
                      Gestor da Área
                    </label>
                    <select
                      value={formData.managerId}
                      onChange={(e) => setFormData({ ...formData, managerId: e.target.value })}
                      className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary"
                    >
                      <option value="">Selecionar gestor (opcional)</option>
                      {availableManagers.map((manager) => (
                        <option key={manager.id} value={manager.id}>
                          {manager.name} ({manager.role})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-text-secondary">
                    Descrição
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                    placeholder="Descrição da área..."
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createAreaMutation.isPending}
                    className="px-4 py-2 border border-transparent rounded-button shadow-glass text-sm font-bold text-white bg-gradient-to-r from-primary to-secondary hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-secondary"
                  >
                    {createAreaMutation.isPending ? 'Criando...' : 'Criar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição */}
      {showEditModal && selectedArea && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-glass rounded-button bg-background-glass backdrop-blur-glass">
            <div className="mt-3">
              <h3 className="text-lg font-bold text-text-primary mb-4">
                Editar Área
              </h3>
              <form onSubmit={handleSubmitEdit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-text-secondary">
                      Nome da Área *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-text-secondary">
                      Gestor da Área
                    </label>
                    <select
                      value={formData.managerId}
                      onChange={(e) => setFormData({ ...formData, managerId: e.target.value })}
                      className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary"
                    >
                      <option value="">Selecionar gestor (opcional)</option>
                      {availableManagers.map((manager) => (
                        <option key={manager.id} value={manager.id}>
                          {manager.name} ({manager.role})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-text-secondary">
                    Descrição
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full border-border-glass rounded-button shadow-glass focus:ring-secondary focus:border-secondary/50 sm:text-sm bg-background-glass text-text-primary placeholder-text-muted"
                  />
                </div>
                
                {/* Toggle Ativar/Inativar */}
                <div className="border-t border-border-glass pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-sm font-bold text-text-secondary">
                        Status da Área
                      </label>
                      <p className="text-xs text-text-muted mt-1">
                        {formData.active ? 'Área ativa e disponível para uso' : 'Área inativa - não aparecerá nas listagens'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, active: !formData.active })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.active ? 'bg-green-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formData.active ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  {formData.active !== selectedArea?.active && (
                    <div className={`mt-2 p-3 rounded-lg ${
                      formData.active ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'
                    }`}>
                      <p className={`text-sm ${formData.active ? 'text-green-400' : 'text-red-400'}`}>
                        {formData.active ? (
                          <>✓ Esta área será <strong>ativada</strong> e ficará disponível para uso.</>
                        ) : (
                          <>⚠ Esta área será <strong>inativada</strong> e não aparecerá nas listagens.</>
                        )}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={updateAreaMutation.isPending}
                    className="px-4 py-2 border border-transparent rounded-button shadow-glass text-sm font-bold text-white bg-gradient-to-r from-primary to-secondary hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-secondary"
                  >
                    {updateAreaMutation.isPending ? 'Salvando...' : 'Atualizar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
